import { PinIcon } from '@eleveo/icons-react';
import classnames from 'classnames';
import React, { SyntheticEvent } from 'react';
import { DraggableProvided, DraggableStateSnapshot } from 'react-beautiful-dnd';

import { DEFAULT_OUTLINE_COLOR_OPACITY, formatScheduleElementRGBAColor, getContrastColor } from '../../../utils/chart';
import { getElementPropTag } from '../constants';
import { ScheduleElement } from '../types';

export interface DraggableItemProps {
  draggedElementStyle: { width?: number };
  providedDraggable: DraggableProvided;
  snapshotDraggable: DraggableStateSnapshot;
  item: ScheduleElement;
  itemStyle: React.CSSProperties;
  parentStyle: {};
  index: number;
}

export const DraggableItem = ({
  draggedElementStyle,
  providedDraggable,
  snapshotDraggable,
  item,
  itemStyle,
  parentStyle,
  index,
}: DraggableItemProps) => {
  // move cloned element so it would be centered related to the current cursor position
  const onMouseDownHandler = (e: SyntheticEvent<HTMLElement, MouseEvent>) => {
    // handle only left button's clicks
    if (e.nativeEvent.which === 1) {
      const rect = ((e.nativeEvent.target! as HTMLElement).parentNode as HTMLElement).getBoundingClientRect();
      const draggingElementWidth = draggedElementStyle.width || 0;
      const draggableItemParent = (e.nativeEvent.target as HTMLElement).parentNode as HTMLElement;

      if (draggableItemParent && draggableItemParent.hasChildNodes()) {
        const draggable = draggableItemParent.firstElementChild as HTMLElement;

        draggable.style.left = `${e.nativeEvent.clientX - rect.left - draggingElementWidth / 2}px`;
        draggable.style.top = `${e.nativeEvent.clientY - rect.top - (rect.bottom - rect.top) / 2}px`;
      }
    }
  };

  const onMouseUpHandler = (e: SyntheticEvent<HTMLElement, MouseEvent>) => {
    // handle only left button's clicks
    if (e.nativeEvent.which === 1) {
      const draggableItemParent = (e.nativeEvent.target as HTMLElement).parentNode as HTMLElement;

      if (draggableItemParent && draggableItemParent.hasChildNodes()) {
        const draggable = draggableItemParent.firstElementChild as HTMLElement;

        draggable.style.left = `0px`;
        draggable.style.top = `0px`;
      }
    }
  };

  const draggingElementClasses = classnames(
    'draggableItemParent',
    { dragged: snapshotDraggable.isDragging },
    { dropped: snapshotDraggable.isDropAnimating }
  );

  return (
    <div className="list-element-wrapper" onMouseDown={onMouseDownHandler} onMouseUp={onMouseUpHandler}>
      <div
        data-test-id={getElementPropTag(index, item.name)}
        ref={providedDraggable.innerRef}
        {...providedDraggable.draggableProps}
        {...providedDraggable.dragHandleProps}
        className={draggingElementClasses}
        style={snapshotDraggable.isDragging ? { ...parentStyle, ...draggedElementStyle } : parentStyle}
      >
        {item.name}
      </div>

      <div data-test-id={`draggable-element:${item.name}`} className="clone draggableItem" style={itemStyle}>
        {item.name}
        {item.pinnable && (
          <PinIcon
            className="ml-1"
            data-test-id={`draggable-element:${item.name}:pinnable`}
            style={{
              stroke: formatScheduleElementRGBAColor(
                getContrastColor(getContrastColor(item.color)),
                DEFAULT_OUTLINE_COLOR_OPACITY
              ),
              strokeWidth: 2,
            }}
          />
        )}
      </div>
    </div>
  );
};
